// deps
import React, { memo, useCallback } from "react";

// components
import PieChartOutline from "./icons/PieChartOutline";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// interfaces
import { ISplitModeOption, SplitMethod } from "../../../../interfaces";

// constants
import { RUPEE_SYMBOL, PERCENTAGE_SYMBOL } from "../../../../constants";
import { SPLIT_BILL_TEST_IDS } from "@swiggy-private/payment-test-ids";

//styles
import { Tab, TabLabelRow, TabUnderline, TabsContainer } from "./styled";

const TAB_TEST_ID_MAP: Record<string, string> = {
    [SplitMethod.AMOUNT]: SPLIT_BILL_TEST_IDS.TAB_AMOUNT,
    [SplitMethod.SHARE]: SPLIT_BILL_TEST_IDS.TAB_SHARE,
    [SplitMethod.PERCENTAGE]: SPLIT_BILL_TEST_IDS.TAB_PERCENTAGE,
};

interface ISplitBillTabProps {
    label: string;
    isActive: boolean;
    type: SplitMethod;
    onSelectTab: (method: SplitMethod) => void;
    leading?: React.ReactNode;
    testId?: string;
}

const SplitBillTab: React.FC<ISplitBillTabProps> = memo(function SplitBillTab({
    label,
    isActive,
    type,
    onSelectTab,
    leading,
    testId,
}) {
    const onClick = useCallback(() => onSelectTab(type), [onSelectTab, type]);
    return (
        <Tab onClick={onClick} data-testid={testId}>
            <TabLabelRow>
                {leading}
                <Text
                    variant="V2.body2.neutral.medium"
                    color={isActive ? "text_Highest_Emphasis" : "text_Med_Emphasis"}
                >
                    {label}
                </Text>
            </TabLabelRow>
            {isActive && <TabUnderline />}
        </Tab>
    );
});

export interface ISplitBillTabsProps {
    splitModeOptions: ISplitModeOption[];
    selectedTab: SplitMethod;
    onSelectTab: (method: SplitMethod) => void;
}

const getLeadingForMethod = (method: SplitMethod, isActive: boolean) => {
    const emphasisColor = isActive ? "text_Highest_Emphasis" : "text_Med_Emphasis";
    const textLeading = (value: string) => (
        <Text variant="V2.body1.neutral.medium" color={emphasisColor}>
            {value}
        </Text>
    );

    switch (method) {
        case SplitMethod.AMOUNT:
            return textLeading(RUPEE_SYMBOL);
        case SplitMethod.PERCENTAGE:
            return textLeading(PERCENTAGE_SYMBOL);
        case SplitMethod.SHARE:
            return <PieChartOutline isActive={isActive} />;
        default:
            return null;
    }
};

const SplitBillTabs: React.FC<ISplitBillTabsProps> = ({ splitModeOptions, selectedTab, onSelectTab }) => {
    return (
        <TabsContainer>
            {splitModeOptions.map((option) => {
                const isActive = selectedTab === option.type;
                return (
                    <SplitBillTab
                        key={option.type}
                        label={option.displayText}
                        isActive={isActive}
                        type={option.type}
                        onSelectTab={onSelectTab}
                        leading={getLeadingForMethod(option.type, isActive)}
                        testId={TAB_TEST_ID_MAP[option.type]}
                    />
                );
            })}
        </TabsContainer>
    );
};

export default memo(SplitBillTabs);
